import { css } from '@emotion/react';
import { ColDef, ColumnVisibleEvent, FirstDataRenderedEvent, IsFullWidthRowParams } from 'ag-grid-community';
import { atom, useAtomValue, useStore } from 'jotai';
import { set } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { AgTableWithBaseStyle } from '../../../../../components/AgTable/AgTable2';
import {
  ICompanyFinancialsDataModel,
  KpiPeriod,
  KpiSection,
} from '../../../../../data-models/company-financials.data-model';
import { IField } from '../../../../../data-models/field2.data-model';
import { PermissionKey } from '../../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../../services/PermissionService';
import {
  kpiConfigByIdMapAtom,
  kpiDefinitionByNameAtom,
  kpiGroupsByIdAtom,
} from '../../../../../services/state/KPIConfigState';
import { colors } from '../../../../../theme/colors';
import { companyProfileFinancialsDefaultColDef } from '../../../commonGridColDefs';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';
import {
  financialsCurrencyOptionsForIdAtom,
  selectedFinancialsCurrencyForIdAtom,
} from '../hooks/FinancialsCurrencyState';
import { financialsFormatterOverridesAtom } from '../hooks/FinancialsKpiDisplayState';
import { useFinancialsGrid2Save } from '../hooks/useFinancialsGrid2Save';
import { useFinancialsGridData } from '../hooks/useFinancialsGridData';
import { KpiType } from '../FinancialsTypeToggle';
import {
  addPinnedYearsColDef,
  anchorColumnChooserToAddButton,
  DEFAULT_PINNED_YEAR_INDICES,
  FinancialsGridRowData,
  FinancialsRowKind,
  financialsCellToClipboard,
  financialsToRowData,
  findColumnClosestToDate,
  getCompanyProfileFinancialsColDefs,
  getCompanyProfileKpiTreeColDef,
  getFinancialsDataPath,
  getFinancialsGridIsoDates,
  getFinancialsHeaderData,
  getFinancialsRowClass,
  getFinancialsRowId,
  getPinnedYearEndDates,
  getPinnedYearHeaderData,
  PINNED_YEAR_CELL_CLASS,
  PINNED_YEAR_HEADER_CLASS,
  pinnedYearSelectionFromEvent,
  restrictPasteToSingleCell,
  sortFinancialsRowData,
} from './FinancialsGrid2Defs';
import { FinancialsGridNoRowsOverlay } from './FinancialsGridNoRowsOverlay';
import { FinancialsDisplaySettings, useFinancialsGridSettings } from './FinancialsSettingsState';

const gridStyles = css`
  height: 100%;
  /* without this the overlay's "Add Financials and KPIs" button cannot be clicked (legacy finTableStyles) */
  .ag-overlay .ag-react-container {
    background-color: transparent;
    pointer-events: auto;
  }
  /* ag-grid top-aligns the group cell's children (for wrapped text); the actual row's KPI cell is a
     group cell here and its single-line name has to sit level with the chevron */
  .ag-cell-wrapper.ag-row-group {
    align-items: center;
  }
  /* the base grid style tints group rows, which under tree data includes every actual row; keep the tint
     for the group row (level 0) only, so a KPI's rows look like the data rows they are */
  .ag-row.ag-row-group:is(.ag-row-group-expanded, .ag-row-group-contracted):not(.ag-row-level-0) {
    background-color: var(--ag-data-background-color);
    &.ag-row-odd {
      background-color: var(--ag-odd-row-background-color);
    }
  }
  /* the pinned fiscal years read as one block, as in the legacy table; its rows are translucent over a tinted
     container, ours are opaque, so the lighter tint goes on the cells themselves */
  .${PINNED_YEAR_HEADER_CLASS} {
    background-color: ${colors.primary[20]}88;
  }
  .${PINNED_YEAR_CELL_CLASS} {
    background-color: ${colors.primary[20]}22;
  }
  /* eases the editor and ag-grid's editing shadow in when editing starts; the shadow's ease-out on stop does not
     show in this grid (a vanilla headless grid transitions both ways; cause not chased) */
  .ag-cell {
    transition: box-shadow 120ms ease-out;
  }
  @keyframes financials-editor-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  /* the inline editor is the editing cell's only child: period cells have no cell wrapper */
  .ag-cell-inline-editing > * {
    animation: financials-editor-in 100ms ease-out;
  }
  @media (prefers-reduced-motion: reduce) {
    .ag-cell {
      transition: none;
    }
    .ag-cell-inline-editing > * {
      animation: none;
    }
  }
`;

/**
 * Which fiscal years are pinned, seeded into the columns when they are created and written back from the grid.
 * Module level as in the legacy table: shared by every company, in memory only, gone on reload.
 */
const pinnedYearSelectionAtom = atom<number[]>(DEFAULT_PINNED_YEAR_INDICES);

// long enough that scanning a row of numbers stays quiet, as in the repo's other grids (ag-grid's own is 2s)
const TOOLTIP_SHOW_DELAY = 1000;

/** A group row is a full-width band as in the legacy grid; the group renderer draws its name and chevron. */
function isKpiGroupRow({ rowNode }: IsFullWidthRowParams<FinancialsGridRowData>) {
  return rowNode.level === 0;
}
// Enter after an edit moves focus onto the band; without this a second Enter collapses the group
const groupBandRendererParams = { suppressCount: true, suppressEnterExpand: true };

interface IFinancialsGrid2Props {
  endDate: Date | null;
  financials: ICompanyFinancialsDataModel[];
  fye: number;
  nSelectedPeriods: number;
  period: KpiPeriod;
}
export function FinancialsGrid2({
  endDate,
  financials,
  fye,
  nSelectedPeriods,
  period,
}: IFinancialsGrid2Props) {
  const hasAuditorAccess = PermissionService.get().hasPermission(PermissionKey.hasAuditorAccess);
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const selectedCurrency = useAtomValue(selectedFinancialsCurrencyForIdAtom(companyId));
  const currencyOptions = useAtomValue(financialsCurrencyOptionsForIdAtom(companyId));
  // options[0] is the native currency; a converted view is read-only, as in the legacy grid
  const isNativeCurrency = selectedCurrency === currencyOptions[0];
  const formatterOverrides = useAtomValue(financialsFormatterOverridesAtom);
  const { displaySettings } = useFinancialsGridSettings();
  const store = useStore();
  const pinnedYearDates = useMemo(
    () => (displaySettings.showPinnedYears ? getPinnedYearEndDates(fye, new Date()) : []),
    [displaySettings.showPinnedYears, fye]
  );
  const headerData = useMemo(() => {
    const isoDates = getFinancialsGridIsoDates({ nSelectedPeriods, period, fye, endDate });
    // read, not subscribed: the seed only has to be current when the columns below are created
    const selectedIndices = store.get(pinnedYearSelectionAtom);
    return [
      ...getFinancialsHeaderData(fye, isoDates, period),
      ...getPinnedYearHeaderData({ fye, dates: pinnedYearDates, selectedIndices }),
    ];
  }, [nSelectedPeriods, period, fye, endDate, pinnedYearDates, store]);
  const financialsByKpiIdPeriodSectionDate = useMemo(() => {
    return financials.reduce((acc, curr) => {
      set(acc, [curr.kpiId, curr.period, curr.section, curr.date], curr);
      return acc;
    }, {});
  }, [financials]);
  const kpisById = useAtomValue(kpiConfigByIdMapAtom);
  const kpisByName = useAtomValue(kpiDefinitionByNameAtom);
  const kpiGroupMap = useAtomValue(kpiGroupsByIdAtom);
  const { getFinancialsRowData } = useFinancialsGridData();
  // The legacy hook decides which KPIs are shown (admin display prefs, reportedOnly). Its callback
  // gets a new identity every render, so the id list is reduced to a string and memoised on that.
  const displayedKpiIdsKey = useMemo(() => {
    return getFinancialsRowData({ period, displaySettings, type: KpiType.Quantitative })
      .map((kpi) => kpi.id)
      .join(',');
  }, [getFinancialsRowData, period, displaySettings]);
  const kpiMap = useMemo(() => {
    return kpiMapFromIdsKey(displayedKpiIdsKey, kpisById);
  }, [displayedKpiIdsKey, kpisById]);
  const include = useMemo(() => {
    return displaySettingsToInclude(displaySettings);
  }, [displaySettings]);
  const rowData = useMemo(() => {
    const rows = financialsToRowData({
      financials,
      include,
      kpiMap,
      kpiGroupMap,
      period,
      pinnedYears: { dates: pinnedYearDates, financialsByKey: financialsByKpiIdPeriodSectionDate },
      readOnly: false,
    });
    return sortFinancialsRowData(rows, { kpisByName, kpiGroupsById: kpiGroupMap });
  }, [
    financials,
    kpiMap,
    kpiGroupMap,
    kpisByName,
    period,
    include,
    pinnedYearDates,
    financialsByKpiIdPeriodSectionDate,
  ]);
  const kpiTreeColDef = useMemo(() => {
    return getCompanyProfileKpiTreeColDef({ kpisByName, kpiGroupsById: kpiGroupMap });
  }, [kpisByName, kpiGroupMap]);
  const columnDefs = useMemo(() => {
    const defs = getCompanyProfileFinancialsColDefs({
      headerData,
      financialsByKey: financialsByKpiIdPeriodSectionDate,
      hasAuditorAccess,
      isNativeCurrency,
      kpisById,
      formatterOverrides,
      selectedCurrency,
      showGrowth: displaySettings.showGrowth,
    });
    return pinnedYearDates.length > 0 ? [...defs, addPinnedYearsColDef] : defs;
  }, [
    pinnedYearDates,
    headerData,
    financialsByKpiIdPeriodSectionDate,
    hasAuditorAccess,
    isNativeCurrency,
    kpisById,
    formatterOverrides,
    selectedCurrency,
    displaySettings.showGrowth,
  ]);
  const onCellValueChanged = useFinancialsGrid2Save(financialsByKpiIdPeriodSectionDate, kpisById);
  // the grid owns which years are shown; this only keeps the seed for the next time the columns are created
  const onColumnVisible = useCallback(
    (event: ColumnVisibleEvent<FinancialsGridRowData>) => {
      const selection = pinnedYearSelectionFromEvent(event);
      if (selection) {
        store.set(pinnedYearSelectionAtom, selection);
      }
    },
    [store]
  );
  const onFirstDataRendered = useCallback((event: FirstDataRenderedEvent<FinancialsGridRowData>) => {
    const column = findColumnClosestToDate(event.api.getAllDisplayedColumns(), Date.now());
    if (column) {
      event.api.ensureColumnVisible(column, 'end');
    }
  }, []);

  return (
    <div css={gridStyles}>
      <AgTableWithBaseStyle
        columnDefs={columnDefs}
        defaultColDef={companyProfileFinancialsDefaultColDef}
        rowData={rowData}
        treeData={true}
        getDataPath={getFinancialsDataPath}
        autoGroupColumnDef={kpiTreeColDef}
        getRowId={getFinancialsRowId}
        getRowClass={getFinancialsRowClass}
        groupDefaultExpanded={-1}
        suppressGroupRowsSticky={true}
        isFullWidthRow={isKpiGroupRow}
        fullWidthCellRenderer='agGroupCellRenderer'
        fullWidthCellRendererParams={groupBandRendererParams}
        cellSelection={{ suppressMultiRanges: true }}
        processCellForClipboard={financialsCellToClipboard}
        processDataFromClipboard={restrictPasteToSingleCell}
        suppressLastEmptyLineOnPaste={true}
        suppressCutToClipboard={true}
        suppressMovableColumns={true}
        stopEditingWhenCellsLoseFocus={true}
        enterNavigatesVerticallyAfterEdit={true}
        tooltipShowDelay={TOOLTIP_SHOW_DELAY}
        postProcessPopup={anchorColumnChooserToAddButton}
        onCellValueChanged={onCellValueChanged}
        onColumnVisible={onColumnVisible}
        onFirstDataRendered={onFirstDataRendered}
        noRowsOverlayComponent={FinancialsGridNoRowsOverlay}
      />
    </div>
  );
}

/** Still used by the KPI request grid (KPIAgGrid2); the Company Profile grid groups through its own defs. */
export const autoGroupColumnDef: ColDef<FinancialsGridRowData> = {
  headerName: 'KPI',
  filter: true,
  sortable: true,
  initialSort: 'asc',
  minWidth: 200,
  width: 300,
  maxWidth: 300,
};

function kpiMapFromIdsKey(idsKey: string, kpisById: Map<number, IField<unknown>>) {
  const ids = idsKey === '' ? [] : idsKey.split(',').map(Number);
  return new Map(
    ids.flatMap((id) => {
      const kpi = kpisById.get(id);
      return kpi ? [[id, kpi] as const] : [];
    })
  );
}

export function displaySettingsToInclude(displaySettings: FinancialsDisplaySettings) {
  const include = new Set<FinancialsRowKind>([KpiSection.actual]);
  if (displaySettings.showBudget) {
    include.add(KpiSection.budget);
  }
  if (displaySettings.showLTM) {
    include.add('LTM');
  }
  if (displaySettings.showYTD) {
    include.add('CYTD');
  }
  return include;
}
